import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Plus, Trash2, Edit, X, Save, Lock, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../lib/api';

export function CategoriesManager() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formName, setFormName] = useState('');
  const [formPremium, setFormPremium] = useState(false);
  const [formOrder, setFormOrder] = useState(100);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (error) {
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = () => {
    setEditingCategory(null);
    setFormName('');
    setFormPremium(false);
    setFormOrder(categories.length + 1);
    setShowForm(true);
  };

  const openEditForm = (category) => {
    setEditingCategory(category);
    setFormName(category.name);
    setFormPremium(category.premium);
    setFormOrder(category.order || 100);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    setSaving(true);
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/categories/${editingCategory.id}`,
          { name: formName, premium: formPremium, order: parseInt(formOrder) },
          { withCredentials: true }
        );
        toast.success('Categoria atualizada');
      } else {
        await axios.post(
          `${API}/categories`,
          { name: formName, premium: formPremium, order: parseInt(formOrder) },
          { withCredentials: true }
        );
        toast.success('Categoria criada');
      }
      setShowForm(false);
      await loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar categoria');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId, categoryName) => {
    if (!window.confirm(`Tem certeza que deseja deletar "${categoryName}"?`)) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { withCredentials: true });
      toast.success('Categoria deletada');
      await loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao deletar categoria');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-zinc-400">Carregando categorias...</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="categories-section"
    >
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white">Gerenciar Categorias</h2>
          <p className="text-zinc-400 text-sm mt-1">
            Crie, edite e organize as categorias da plataforma
          </p>
        </div>
        <button
          data-testid="add-category-button"
          onClick={openCreateForm}
          className="flex items-center space-x-2 bg-[#0066FF] text-white px-6 py-3 rounded-lg font-bold hover:bg-[#3385FF] transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Nova Categoria</span>
        </button>
      </div>

      <div className="backdrop-blur-xl bg-[#121212]/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1A1A1A] border-b border-[#27272A]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-bold text-white">Ordem</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-white">Nome</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-white">ID (slug)</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-white">Acesso</th>
                <th className="px-6 py-4 text-left text-sm font-bold text-white">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272A]">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-zinc-500">
                    Nenhuma categoria cadastrada
                  </td>
                </tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id} data-testid={`category-row-${cat.id}`} className="hover:bg-[#1A1A1A]/50">
                    <td className="px-6 py-4 text-zinc-400 font-mono">{cat.order || '-'}</td>
                    <td className="px-6 py-4 text-white font-semibold">{cat.name}</td>
                    <td className="px-6 py-4 text-zinc-500 font-mono text-sm">{cat.id}</td>
                    <td className="px-6 py-4">
                      {cat.premium ? (
                        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold bg-[#00FF99]/20 text-[#00FF99]">
                          <Lock className="w-3 h-3" />
                          <span>PREMIUM</span>
                        </span>
                      ) : (
                        <span className="inline-flex items-center space-x-1 px-3 py-1 rounded-full text-xs font-bold bg-[#1A1A1A] text-zinc-400">
                          <Globe className="w-3 h-3" />
                          <span>LIVRE</span>
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          data-testid={`edit-category-${cat.id}`}
                          onClick={() => openEditForm(cat)}
                          className="p-2 bg-[#0066FF]/20 border border-[#0066FF]/50 text-[#0066FF] rounded hover:bg-[#0066FF]/30 transition-colors"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-category-${cat.id}`}
                          onClick={() => handleDelete(cat.id, cat.name)}
                          className="p-2 bg-red-500/20 border border-red-500/50 text-red-500 rounded hover:bg-red-500/30 transition-colors"
                          title="Deletar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="category-form-dialog"
            className="w-full max-w-md bg-[#121212] border border-[#27272A] rounded-2xl p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-black text-white tracking-tight">
                {editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 hover:bg-[#1A1A1A] rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">
                  Nome <span className="text-[#0066FF]">*</span>
                </label>
                <input
                  data-testid="category-name-input"
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  required
                  className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
                  placeholder="Ex: Decoração de Festa"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Ordem de Exibição</label>
                <input
                  type="number"
                  min="1"
                  value={formOrder}
                  onChange={(e) => setFormOrder(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-[#0066FF] transition-colors"
                />
                <p className="text-xs text-zinc-500 mt-1">Menor número aparece primeiro na landing page</p>
              </div>

              <label className="flex items-center space-x-3 cursor-pointer bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-3 hover:border-[#00FF99] transition-colors">
                <input
                  data-testid="category-premium-input"
                  type="checkbox"
                  checked={formPremium}
                  onChange={(e) => setFormPremium(e.target.checked)}
                  className="w-4 h-4"
                />
                <div className="flex-1">
                  <span className="text-white font-semibold">Categoria Premium</span>
                  <p className="text-xs text-zinc-500">Apenas usuários premium podem visualizar os modelos</p>
                </div>
                <Lock className={`w-5 h-5 ${formPremium ? 'text-[#00FF99]' : 'text-zinc-600'}`} />
              </label>

              <div className="flex items-center space-x-3 pt-4 border-t border-[#27272A]">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 py-2.5 bg-[#1A1A1A] border border-[#27272A] text-white rounded-lg hover:border-zinc-500 transition-colors font-semibold"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="save-category-button"
                  disabled={saving}
                  className="flex-1 flex items-center justify-center space-x-2 px-6 py-2.5 bg-[#0066FF] text-white rounded-lg hover:bg-[#3385FF] transition-colors font-bold disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{saving ? 'Salvando...' : 'Salvar'}</span>
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
}
